import { useState, type ReactNode } from 'react'

export interface TabDef {
  /** Stable id for the tab. */
  id: string
  /** Label shown in the tab bar. */
  label: ReactNode
  /** Panel content, rendered only while the tab is active. */
  content: ReactNode
}

/**
 * Simple tabbed view used on detail pages to replace several cards stacked on top
 * of each other. Only the active panel is mounted; the surrounding page keeps its
 * own state, so switching tabs doesn't lose form input or reload data. With a
 * single tab the bar is hidden (nothing to switch between).
 */
export function Tabs({ tabs, initialId }: { tabs: TabDef[]; initialId?: string }) {
  const [active, setActive] = useState(initialId ?? tabs[0]?.id)
  const current = tabs.find((t) => t.id === active) ?? tabs[0]

  return (
    <div className="tabs">
      {tabs.length > 1 && (
        <div className="tab-bar" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === current?.id}
              className={t.id === current?.id ? 'tab active' : 'tab'}
              onClick={() => setActive(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className="tab-panel" role="tabpanel">{current?.content}</div>
    </div>
  )
}
